import { useLanguage } from '../contexts/LanguageContext';
import SharedHeader from './SharedHeader';
import Footer from './Footer';

const TermsOfService = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Terms of Service',
      lastUpdated: 'Last updated: January 2025',
      sections: [
        {
          title: 'Acceptance of Terms',
          content: [
            'By accessing and using QRCodeForge.com ("Service"), you accept and agree to be bound by the terms and conditions of this agreement.',
            'If you do not agree to abide by the terms and conditions of this agreement, you may not access or use the Service.',
            'These terms apply to all visitors, users, and others who access or use the Service.'
          ]
        },
        {
          title: 'Description of Service',
          content: [
            'QRCodeForge.com is a free online QR code generator that allows users to create QR codes for various purposes including:',
            '• URL/Website links',
            '• Plain text content',
            '• Email addresses with optional subject and message',
            '• Phone numbers for direct calling',
            '• SMS messages with pre-filled content',
            '• WiFi network credentials',
            '• Social media profiles (Facebook, Twitter)',
            'All QR code generation is performed client-side in your browser for privacy and security.'
          ]
        },
        {
          title: 'User Responsibilities',
          content: [
            'You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:',
            '• Use the Service for any unlawful purpose or to solicit unlawful activity',
            '• Create QR codes containing harmful, illegal, or offensive content',
            '• Generate QR codes that infringe on intellectual property rights',
            '• Attempt to compromise the security or integrity of the Service',
            '• Use the Service to distribute malware, viruses, or malicious code',
            '• Engage in any activity that could harm, disable, or impair the Service'
          ]
        },
        {
          title: 'Content and Intellectual Property',
          content: [
            '• You retain full ownership of any content you input into QR codes',
            '• QRCodeForge.com does not claim ownership of your QR code content',
            '• You are responsible for ensuring you have rights to all content used',
            '• The QR codes generated are yours to use for personal or commercial purposes',
            '• Our website design, code, and branding remain our intellectual property'
          ]
        },
        {
          title: 'Privacy and Data',
          content: [
            '• All QR code generation happens locally in your browser',
            '• We do not store, transmit, or have access to your QR code content',
            '• Usage analytics are collected anonymously through Google Analytics',
            '• Please refer to our Privacy Policy for detailed information about data handling'
          ]
        },
        {
          title: 'Service Availability',
          content: [
            '• We strive to maintain 99.9% uptime but cannot guarantee uninterrupted service',
            '• We reserve the right to modify, suspend, or discontinue the Service at any time',
            '• Scheduled maintenance will be announced when possible',
            '• We are not liable for any loss or damage due to service interruptions'
          ]
        },
        {
          title: 'Disclaimer of Warranties',
          content: [
            'The Service is provided "as is" without warranties of any kind, either express or implied, including:',
            '• No warranty that the Service will meet your requirements',
            '• No warranty that the Service will be uninterrupted or error-free',
            '• No warranty regarding the accuracy or reliability of generated QR codes',
            '• Users are responsible for testing QR codes before use in production'
          ]
        },
        {
          title: 'Limitation of Liability',
          content: [
            'QRCodeForge.com and its operators shall not be liable for any indirect, incidental, special, or consequential damages, including:',
            '• Loss of profits or business opportunities',
            '• Data loss or corruption',
            '• Service interruptions or delays',
            '• Third-party content accessed through generated QR codes',
            'Our total liability is limited to the amount you paid for the Service (which is free).'
          ]
        },
        {
          title: 'Third-Party Services',
          content: [
            '• We use Google Analytics and Google AdSense for analytics and advertising',
            '• These services are governed by their respective terms of service',
            '• We are not responsible for third-party service policies or practices',
            '• Links in generated QR codes lead to external sites beyond our control'
          ]
        },
        {
          title: 'Modifications to Terms',
          content: [
            '• We reserve the right to modify these terms at any time',
            '• Changes will be posted on this page with an updated date',
            '• Continued use of the Service constitutes acceptance of modified terms',
            '• Material changes will be highlighted when possible'
          ]
        },
        {
          title: 'Governing Law',
          content: [
            '• These terms are governed by applicable international laws',
            '• Any disputes will be resolved through appropriate legal channels',
            '• If any provision is deemed unenforceable, the remaining terms remain valid'
          ]
        },
        {
          title: 'Contact Information',
          content: [
            'For questions about these Terms of Service, please contact us through:',
            '• Email: support@qrcodeforge.com',
            '• Website: QRCodeForge.com',
            '• We will respond to inquiries in a timely manner'
          ]
        }
      ]
    },
    zh: {
      title: '使用条款',
      lastUpdated: '最后更新：2025年1月',
      sections: [
        {
          title: '条款接受',
          content: [
            '通过访问和使用 QRCodeForge.com（"服务"），您接受并同意受本协议条款和条件的约束。',
            '如果您不同意遵守本协议的条款和条件，您不得访问或使用本服务。',
            '这些条款适用于访问或使用本服务的所有访客、用户和其他人员。'
          ]
        },
        {
          title: '服务描述',
          content: [
            'QRCodeForge.com 是一个免费的在线二维码生成器，允许用户为各种用途创建二维码，包括：',
            '• 网址/网站链接',
            '• 纯文本内容',
            '• 带有可选主题和消息的电子邮件地址',
            '• 用于直接拨号的电话号码',
            '• 带有预填内容的短信消息',
            '• WiFi 网络凭据',
            '• 社交媒体资料（Facebook、Twitter）',
            '所有二维码生成都在您的浏览器中客户端执行，以保护隐私和安全。'
          ]
        },
        {
          title: '用户责任',
          content: [
            '您同意仅出于合法目的并按照这些条款使用本服务。您同意不：',
            '• 将本服务用于任何非法目的或教唆非法活动',
            '• 创建包含有害、非法或冒犯性内容的二维码',
            '• 生成侵犯知识产权的二维码',
            '• 试图损害本服务的安全性或完整性',
            '• 使用本服务分发恶意软件、病毒或恶意代码',
            '• 从事任何可能损害、禁用或削弱本服务的活动'
          ]
        },
        {
          title: '内容和知识产权',
          content: [
            '• 您保留对输入二维码中的任何内容的完全所有权',
            '• QRCodeForge.com 不声明拥有您的二维码内容的所有权',
            '• 您有责任确保您对所使用的所有内容拥有权利',
            '• 生成的二维码可供您个人或商业使用',
            '• 我们的网站设计、代码和品牌仍为我们的知识产权'
          ]
        },
        {
          title: '隐私和数据',
          content: [
            '• 所有二维码生成都在您的浏览器中本地进行',
            '• 我们不存储、传输或访问您的二维码内容',
            '• 通过 Google Analytics 匿名收集使用分析数据',
            '• 有关数据处理的详细信息，请参阅我们的隐私政策'
          ]
        },
        {
          title: '服务可用性',
          content: [
            '• 我们努力保持 99.9% 的正常运行时间，但不能保证不间断服务',
            '• 我们保留随时修改、暂停或终止服务的权利',
            '• 计划维护将在可能时提前公布',
            '• 我们不对因服务中断造成的任何损失或损害承担责任'
          ]
        },
        {
          title: '免责声明',
          content: [
            '本服务按"原样"提供，不提供任何明示或暗示的保证，包括：',
            '• 不保证服务将满足您的要求',
            '• 不保证服务将不间断或无错误',
            '• 不保证生成的二维码的准确性或可靠性',
            '• 用户有责任在生产中使用前测试二维码'
          ]
        },
        {
          title: '责任限制',
          content: [
            'QRCodeForge.com 及其运营商不对任何间接、附带、特殊或后果性损害承担责任，包括：',
            '• 利润损失或商业机会损失',
            '• 数据丢失或损坏',
            '• 服务中断或延迟',
            '• 通过生成的二维码访问的第三方内容',
            '我们的总责任限于您为服务支付的金额（该服务是免费的）。'
          ]
        },
        {
          title: '第三方服务',
          content: [
            '• 我们使用 Google Analytics 和 Google AdSense 进行分析和广告',
            '• 这些服务受其各自服务条款的约束',
            '• 我们不对第三方服务政策或做法负责',
            '• 生成的二维码中的链接指向我们无法控制的外部网站'
          ]
        },
        {
          title: '条款修改',
          content: [
            '• 我们保留随时修改这些条款的权利',
            '• 更改将发布在此页面上，并显示更新日期',
            '• 继续使用本服务即构成对修改条款的接受',
            '• 重大更改将在可能时突出显示'
          ]
        },
        {
          title: '适用法律',
          content: [
            '• 这些条款受适用国际法律管辖',
            '• 任何争议将通过适当的法律渠道解决',
            '• 如果任何条款被认为无法执行，其余条款仍然有效'
          ]
        },
        {
          title: '联系信息',
          content: [
            '有关这些服务条款的问题，请通过以下方式联系我们：',
            '• 邮箱：support@qrcodeforge.com',
            '• 网站：QRCodeForge.com',
            '• 我们将及时回复询问'
          ]
        }
      ]
    }
  };

  const currentContent = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SharedHeader />

      {/* 动态背景元素 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-10 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      <div className="relative">
        {/* Header */}
        <header className="pt-20 pb-8">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold text-white mb-4">
              {currentContent.title}
            </h1>
            <p className="text-gray-300">
              {currentContent.lastUpdated}
            </p>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 pb-16">
          <div className="space-y-8">
            {currentContent.sections.map((section, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
                <h2 className="text-2xl font-semibold text-white mb-4">
                  {section.title}
                </h2>
                <div className="space-y-3">
                  {section.content.map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      className="text-gray-300 leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: paragraph
                          .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
                          .replace(/• /g, '• ')
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default TermsOfService;